import type { GoldConfig } from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { FeatureFlag } from '@iheartjane/feature-flags';
import type { SearchSuggestionProduct } from '@iheartjane/menu';
import { shouldShowGold as showGold } from '@iheartjane/menu';

import { trackProductListingTap } from '@jane/bloom/services/tracking/track';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { FulfillmentMode } from '@jane/bloom/types/schemas/bloom/fulfillmentMode';
import type { ShoppingPreferences } from '@jane/bloom/types/schemas/bloom/shoppingPreferences';
import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';
import { getJaneGoldTitle } from '@jane/bloom/utils/gold/getJaneGoldTitle';
import { buildAutocompleteProduct } from '@jane/bloom/viewModels/autocompleteProduct';
import type { AlgoliaProduct } from '@jane/search/types';

export function toSuggestionProduct({
  customLanguage,
  featureFlags,
  fulfillmentMode,
  generatePath,
  goldConfig,
  hideDropdown,
  pinnedStore,
  product,
  shoppingPreferences,
}: {
  customLanguage: CustomLanguage;
  featureFlags: Partial<Record<FeatureFlag, boolean>>;
  fulfillmentMode: FulfillmentMode;
  generatePath: (path: string) => string;
  goldConfig: GoldConfig | null;
  hideDropdown: () => void;
  pinnedStore: BloomMenuStore;
  product: SmartSortProduct<AlgoliaProduct>;
  shoppingPreferences: ShoppingPreferences | undefined;
}): SearchSuggestionProduct {
  const ap = buildAutocompleteProduct({
    customLanguage,
    product: product.attributes,
    specials: [],
  });

  const showGoldBadge = Boolean(
    pinnedStore &&
    !!getJaneGoldTitle({ menuProduct: product.attributes }) &&
    showGold({
      appMode: 'bloom',
      goldConfig,
      state: pinnedStore.state,
      storeId: pinnedStore.id,
    })
  );

  return {
    baseImage: ap.baseImage,
    brand: ap.brand,
    imageSrcSet: ap.imageSrcSet,
    kind: ap.kind,
    lineage: ap.lineage,
    name: ap.name,
    onClick: () => {
      hideDropdown();
      product.click();
      trackProductListingTap(
        {
          fulfillmentMode,
          product: ap,
          reservationMode: shoppingPreferences?.reservationMode,
          storeId: pinnedStore.id,
        },
        featureFlags
      );
    },
    onImpress: () => product.impress(),
    productId: ap.product_id,
    showGoldBadge,
    specialLabel: ap.specialLabel,
    to: generatePath(
      `products/${ap.product_id}/${ap.unique_slug ?? ap.product_id}`
    ),
  };
}
